import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShoppingCart, DollarSign, Tag, BarChart3, PieChart as PieChartIcon, Users, CheckCircle2, Clock } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0FA49C', '#F59E0B', '#6366F1', '#EC4899', '#14B8A6', '#8B5CF6', '#F97316', '#06B6D4'];

const StatCard = ({ icon: Icon, label, value, sub, color = '#0FA49C', testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-gray-200 p-5"
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-[#666]">{label}</span>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
    </div>
    <p className="text-2xl font-bold text-[#222]">{value}</p>
    {sub && <p className="text-xs text-[#999] mt-1">{sub}</p>}
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-[#222]">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="font-semibold">
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

export const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.get(`${API}/admin/analytics`, { headers });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]"></div>
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-[#666] py-12">No analytics data available.</p>;
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const timelineData = data.orders_timeline.map(d => ({
    ...d,
    label: formatDate(d.date),
  }));

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-[#222]">Analytics</h2>
        <p className="text-sm text-[#666]">Overview of your store performance</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          icon={ShoppingCart}
          label="Total Orders"
          value={data.total_orders}
          testId="stat-total-orders"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue"
          value={`£${data.total_revenue.toFixed(2)}`}
          sub={data.promo_orders > 0 ? `${data.promo_orders} free (promo)` : undefined}
          color="#16A34A"
          testId="stat-revenue"
        />
        <StatCard
          icon={CheckCircle2}
          label="Processed"
          value={data.processed_orders}
          sub={`${data.pending_orders} pending`}
          color="#6366F1"
          testId="stat-processed"
        />
        <StatCard
          icon={Tag}
          label="Promo Orders"
          value={data.promo_orders}
          color="#F59E0B"
          testId="stat-promo-orders"
        />
        <StatCard
          icon={Users}
          label="Page Views"
          value={data.total_visits}
          color="#EC4899"
          testId="stat-visits"
        />
      </div>

      {/* Orders Over Time Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="orders-timeline-chart">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-[#0FA49C]" />
          <h3 className="font-bold text-[#222]">Orders Over Time</h3>
          <span className="text-xs text-[#999]">Last 30 days</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timelineData}>
              <defs>
                <linearGradient id="orderGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0FA49C" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0FA49C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#999' }}
                tickLine={false}
                axisLine={{ stroke: '#f0f0f0' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#999' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="orders"
                name="Orders"
                stroke="#0FA49C"
                strokeWidth={2}
                fill="url(#orderGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Two Column: Popular Products + Box Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Products */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="popular-products-chart">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#0FA49C]" />
            <h3 className="font-bold text-[#222]">Most Popular Products</h3>
          </div>
          {data.popular_products.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.popular_products} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#444' }}
                    width={140}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Times Ordered" fill="#0FA49C" radius={[0, 6, 6, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-[#999] text-sm text-center py-8">No product data yet</p>
          )}
        </div>

        {/* Box Size Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="box-breakdown-chart">
          <div className="flex items-center gap-2 mb-4">
            <PieChartIcon className="w-5 h-5 text-[#0FA49C]" />
            <h3 className="font-bold text-[#222]">Box Size Breakdown</h3>
          </div>
          {data.box_breakdown.length > 0 ? (
            <div className="h-72 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.box_breakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {data.box_breakdown.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-[#999] text-sm text-center py-8">No box data yet</p>
          )}
        </div>
      </div>

      {/* Promo Code Usage */}
      {data.promo_usage.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="promo-usage-section">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-[#F59E0B]" />
            <h3 className="font-bold text-[#222]">Promo Code Usage</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[#666] uppercase">Code</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[#666] uppercase">Times Used</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-[#666] uppercase">% of Orders</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.promo_usage.map((p) => (
                  <tr key={p.code} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-[#222] bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                        {p.code}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#222]">{p.count}</td>
                    <td className="px-4 py-3 text-[#666]">
                      {data.total_orders > 0 ? ((p.count / data.total_orders) * 100).toFixed(1) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
